import axios from 'axios';
import authService from '../../../services/authService';
import { buildApiUrl } from '../../../config/api';
import { ERROR_MESSAGES } from '../utils/settingsConstants';

/**
 * Service for handling settings-related API operations
 * Manages QuickBooks settings, general settings, and data operations
 */
class SettingsService {
  constructor() {
    // Using buildApiUrl for consistent URL construction
  }

  // Get common headers for authenticated requests
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Fetch QuickBooks settings from admin endpoint
  async fetchQuickBooksSettings() {
    try {
      const response = await axios.get(
        buildApiUrl('admin/settings'),
        { headers: this.getAuthHeaders() }
      );
      
      return response.data.settings || {};
    } catch (error) {
      console.error('Error fetching QuickBooks settings:', error);
      throw error;
    }
  }

  // Save QuickBooks settings to admin endpoint
  async saveQuickBooksSettings(qbSettings, setLoading, showNotification) {
    if (!qbSettings.clientId.trim() || !qbSettings.clientSecret.trim()) {
      showNotification(ERROR_MESSAGES.QB_CREDENTIALS_REQUIRED, 'error');
      return false;
    }

    setLoading(true);
    try {
      console.log('Attempting to save QuickBooks settings...');
      
      await axios.put(
        buildApiUrl('admin/settings'),
        {
          settings: {
            qb_global_client_id: {
              value: qbSettings.clientId.trim(),
              description: 'QuickBooks OAuth Client ID'
            },
            qb_global_client_secret: {
              value: qbSettings.clientSecret.trim(),
              description: 'QuickBooks OAuth Client Secret'
            }
          }
        },
        { headers: this.getAuthHeaders() }
      );
      
      console.log('QuickBooks settings saved successfully');
      showNotification('QuickBooks settings saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving QuickBooks settings:', error);
      
      // Provide specific error messages based on response
      if (error.response?.status === 403) {
        showNotification(ERROR_MESSAGES.PERMISSION_DENIED, 'error');
      } else if (error.response?.status === 500) {
        showNotification(ERROR_MESSAGES.SERVER_ERROR, 'error');
      } else {
        showNotification('Failed to save QuickBooks settings: ' + (error.response?.data?.message || error.message), 'error');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }

  // Export client data (future implementation)
  async exportClientData() {
    try {
      const response = await axios.get(
        buildApiUrl('export/clients'),
        { 
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error exporting client data:', error);
      throw error;
    }
  }

  // Export job data (future implementation)
  async exportJobData() {
    try {
      const response = await axios.get(
        buildApiUrl('export/jobs'),
        { 
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'jobs-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error exporting job data:', error);
      throw error;
    }
  }

  // Delete all data (future implementation)
  async deleteAllData() {
    try {
      await axios.delete(
        buildApiUrl('admin/data/all'),
        { headers: this.getAuthHeaders() }
      );
      
      return true;
    } catch (error) {
      console.error('Error deleting all data:', error);
      throw error;
    }
  }
}

export default new SettingsService();